import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../lib/permissions';
import supabase from '../../lib/supabaseClient';
import {
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Database,
  RefreshCw,
  Clock,
  User,
  Bot,
  Settings,
  MessageSquare,
  TrendingUp,
  UserX,
  X,
  Check
} from 'lucide-react';

// Define quick date filter options
const quickDateOptions = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 days' },
  { value: '30days', label: 'Last 30 days' },
  { value: '90days', label: 'Last 90 days' }
];

// Define action types configuration
const actionTypes = {
  'ai_sent_sms': { label: 'AI Sent SMS', icon: MessageSquare, colorClass: 'bg-blue-50', textColorClass: 'text-blue-600' },
  'ai_sent_email': { label: 'AI Sent Email', icon: MessageSquare, colorClass: 'bg-blue-50', textColorClass: 'text-blue-600' },
  'user_created': { label: 'User Created', icon: User, colorClass: 'bg-green-50', textColorClass: 'text-green-600' },
  'user_updated': { label: 'User Updated', icon: User, colorClass: 'bg-yellow-50', textColorClass: 'text-yellow-600' },
  'user_deleted': { label: 'User Deleted', icon: UserX, colorClass: 'bg-red-50', textColorClass: 'text-red-600' },
  'lead_created': { label: 'Lead Created', icon: TrendingUp, colorClass: 'bg-green-50', textColorClass: 'text-green-600' },
  'lead_updated': { label: 'Lead Updated', icon: TrendingUp, colorClass: 'bg-yellow-50', textColorClass: 'text-yellow-600' },
  'system_action': { label: 'System Action', icon: Settings, colorClass: 'bg-gray-50', textColorClass: 'text-gray-600' }
};

// Performed by options
const performedByOptions = [
  { value: '', label: 'All' },
  { value: 'ai', label: 'AI System' },
  { value: 'system', label: 'System' },
  { value: 'user:', label: 'Users' },
  { value: 'webhook:', label: 'Webhooks' }
];

// Dropdown component
const Dropdown = React.memo(function Dropdown({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  className = "" 
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const selectedOption = options.find(opt => opt.value === value);
  
  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white flex items-center justify-between"
      >
        <span className={selectedOption?.value ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between text-sm"
              >
                <span>{option.label}</span>
                {value === option.value && <Check className="w-4 h-4 text-blue-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

// Filter component
const FilterInputs = React.memo(function FilterInputs({
  actionFilter,
  performedByFilter,
  onActionFilterChange,
  onPerformedByFilterChange,
  dateFromFilter,
  dateToFilter,
  handleDateFromChange,
  handleDateToChange,
  quickDateFilter,
  handleQuickDateFilter,
  clearFilters,
  actionOptions
}) {
  const hasActiveFilters = actionFilter || performedByFilter || dateFromFilter || dateToFilter;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Filter className="w-5 h-5 text-gray-500" />
        <h3 className="font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            <X className="w-3 h-3" />
            <span>Clear All</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
          <Dropdown
            options={actionOptions}
            value={actionFilter}
            onChange={onActionFilterChange}
            placeholder="All actions"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Performed By</label>
          <Dropdown
            options={performedByOptions}
            value={performedByFilter}
            onChange={onPerformedByFilterChange}
            placeholder="All performers"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
          <input
            type="date"
            value={dateFromFilter}
            onChange={handleDateFromChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
          <input
            type="date"
            value={dateToFilter}
            onChange={handleDateToChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 mr-2">Quick filters:</span>
        {quickDateOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleQuickDateFilter(option.value)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              quickDateFilter === option.value
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
});

export default function AuditLogViewer() {
  const { user, hasPermission, loading: authLoading } = useAuth();

  // State Management
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  // UI States
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filter States - start with no data showing
  const [actionFilter, setActionFilter] = useState('');
  const [performedByFilter, setPerformedByFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [quickDateFilter, setQuickDateFilter] = useState('');
  const [hasUserSetFilters, setHasUserSetFilters] = useState(false);

  // Dynamic action options based on available data
  const actionOptions = [
    { value: '', label: 'All actions' },
    ...Object.entries(actionTypes).map(([key, config]) => ({
      value: key,
      label: config.label
    }))
  ];

  const canViewAuditLogs = hasPermission(PERMISSIONS.VIEW_EXPORT_MESSAGE_LOGS) || user?.role === 'business_admin';

  // Fetch available actions for dropdown - REMOVED, using static list from actionTypes

  // Filter change handlers
  const handleActionFilterChange = useCallback((value) => {
    setActionFilter(value);
    setCurrentPage(1);
    setHasUserSetFilters(true);
  }, []);

  const handlePerformedByFilterChange = useCallback((value) => {
    setPerformedByFilter(value);
    setCurrentPage(1);
    setHasUserSetFilters(true);
  }, []);

  const handleDateFromChange = useCallback((e) => {
    setDateFromFilter(e.target.value);
    setQuickDateFilter('');
    setCurrentPage(1);
    setHasUserSetFilters(true);
  }, []);

  const handleDateToChange = useCallback((e) => {
    setDateToFilter(e.target.value);
    setQuickDateFilter('');
    setCurrentPage(1);
    setHasUserSetFilters(true);
  }, []);

  const handleQuickDateFilter = useCallback((value) => {
    const today = new Date();
    const utcToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    setQuickDateFilter(value);
    setHasUserSetFilters(true);

    switch (value) {
      case 'today': {
        const str = utcToday.toISOString().split('T')[0];
        setDateFromFilter(str);
        setDateToFilter(str);
        break;
      }
      case '7days': {
        const past = new Date(utcToday);
        past.setDate(past.getDate() - 7);
        setDateFromFilter(past.toISOString().split('T')[0]);
        setDateToFilter(utcToday.toISOString().split('T')[0]);
        break;
      }
      case '30days': {
        const past = new Date(utcToday);
        past.setDate(past.getDate() - 30);
        setDateFromFilter(past.toISOString().split('T')[0]);
        setDateToFilter(utcToday.toISOString().split('T')[0]);
        break;
      }
      case '90days': {
        const past = new Date(utcToday);
        past.setDate(past.getDate() - 90);
        setDateFromFilter(past.toISOString().split('T')[0]);
        setDateToFilter(utcToday.toISOString().split('T')[0]);
        break;
      }
      default:
        setDateFromFilter('');
        setDateToFilter('');
    }
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setActionFilter('');
    setPerformedByFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setQuickDateFilter('');
    setCurrentPage(1);
    setHasUserSetFilters(false);
  }, []);

  const fetchAuditLogs = useCallback(async (showLoadingSpinner = true) => {
    if (!canViewAuditLogs || authLoading) {
      setLoading(false);
      return;
    }

    // Don't fetch data until user has set filters
    if (!hasUserSetFilters) {
      setLoading(false);
      return;
    }

    if (showLoadingSpinner) setLoading(true);
    else setRefreshing(true);
    setError('');

  try {
  // Get total count first
  const { count } = await supabase
    .from('audit_logs')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', user.tenant_id);

  // Get data with RPC function
  const { data, error } = await supabase.rpc('get_audit_logs_with_messages', {
    p_tenant_id: user.tenant_id,
    p_action: actionFilter || null,
    p_performed_by: performedByFilter || null,
    p_date_from: dateFromFilter ? new Date(dateFromFilter + 'T00:00:00.000Z').toISOString() : null,
    p_date_to: dateToFilter ? new Date(dateToFilter + 'T23:59:59.999Z').toISOString() : null,
    p_limit: pageSize,
    p_offset: (currentPage - 1) * pageSize
  });
console.log('RPC data:', data?.[0]); // Log first record to see structure
  if (error) throw new Error(error.message);

  setLogs(data || []);
  setTotalCount(count || 0);
    } catch (err) {
      setError('Failed to load audit logs. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, actionFilter, performedByFilter, dateFromFilter, dateToFilter, canViewAuditLogs, authLoading, user?.tenant_id, pageSize, hasUserSetFilters]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleRefresh = useCallback(() => {
    fetchAuditLogs(false);
  }, [fetchAuditLogs]);

  const formatTimestamp = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  }, []);

  const formatPerformedBy = useCallback((performedBy) => {
    if (performedBy === 'ai') return 'AI System';
    if (performedBy === 'system') return 'System';
    if (performedBy?.startsWith('user:')) return `User ${performedBy.split(':')[1].slice(0, 8)}`;
    if (performedBy?.startsWith('webhook:')) return `Webhook (${performedBy.split(':')[1]})`;
    return performedBy || 'Unknown';
  }, []);

  const getPerformedByIcon = useCallback((performedBy) => {
    if (performedBy === 'ai') return Bot;
    if (performedBy === 'system') return Settings;
    if (performedBy?.startsWith('user:')) return User;
    if (performedBy?.startsWith('webhook:')) return Database;
    return AlertCircle;
  }, []);

  const handleCopyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  }, []);

  const totalPages = Math.ceil(totalCount / pageSize);
  const showingFrom = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const showingTo = Math.min(currentPage * pageSize, totalCount);

  if (!canViewAuditLogs && !authLoading) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-600">You don't have permission to view audit logs.</p>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Audit Logs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Real-time platform activity and changes
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <FilterInputs
        actionFilter={actionFilter}
        performedByFilter={performedByFilter}
        onActionFilterChange={handleActionFilterChange}
        onPerformedByFilterChange={handlePerformedByFilterChange}
        dateFromFilter={dateFromFilter}
        dateToFilter={dateToFilter}
        handleDateFromChange={handleDateFromChange}
        handleDateToChange={handleDateToChange}
        quickDateFilter={quickDateFilter}
        handleQuickDateFilter={handleQuickDateFilter}
        clearFilters={clearFilters}
        actionOptions={actionOptions}
      />

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {totalCount > 0 ? (
            <>Showing {showingFrom}-{showingTo} of {totalCount.toLocaleString()} records</>
          ) : (
            'No records found'
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {!hasUserSetFilters ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select filters to view audit logs</h3>
          <p className="text-gray-600">
            Choose an action type, date range, or performer to view audit log entries.
          </p>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
          <p className="text-gray-600">
            {actionFilter || performedByFilter || dateFromFilter || dateToFilter
              ? 'No logs match your current filters. Try adjusting your search criteria.'
              : 'No audit logs have been recorded yet for your tenant.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lead ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => {
                  const actionConfig = actionTypes[log.action] || { 
                    label: log.action, 
                    icon: AlertCircle, 
                    colorClass: 'bg-gray-50',
                    textColorClass: 'text-gray-600'
                  };

                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${actionConfig.colorClass}`}>
                            {React.createElement(actionConfig.icon, { 
                              className: `w-4 h-4 ${actionConfig.textColorClass}` 
                            })}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">
                              {actionConfig.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              {log.action}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          {React.createElement(getPerformedByIcon(log.performed_by), { 
                            className: "w-4 h-4 text-gray-500" 
                          })}
                          <span className="text-sm text-gray-900">
                            {formatPerformedBy(log.performed_by)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {log.lead_id ? (
                          <div className="text-sm">
                            <span className="font-mono text-blue-600">
                              {log.lead_id.slice(0, 8)}...
                            </span>
                            <button
                              onClick={() => handleCopyToClipboard(log.lead_id)}
                              className="ml-2 text-xs text-gray-500 hover:text-gray-700"
                              title="Copy full ID"
                            >
                              copy
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
  {log.message_id && log.message_id.toString().trim() !== '' ? (
    <div className="text-sm">
      <span className="font-mono text-purple-600">
        {log.message_id.toString().slice(0, 8)}...
      </span>
      <button
        onClick={() => handleCopyToClipboard(log.message_id.toString())}
        className="ml-2 text-xs text-gray-500 hover:text-gray-700"
        title="Copy full ID"
      >
        copy
      </button>
    </div>
  ) : (
    <span className="text-sm text-gray-400">—</span>
  )}
</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span>{formatTimestamp(log.created_at)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {log.details ? (
                          <button
                            onClick={() => {
                              setSelectedLog(log);
                              setShowDetailsModal(true);
                            }}
                            className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            <span>View</span>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>
          
          <span className="text-sm text-gray-600 px-4">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <span>Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {showDetailsModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Audit Log Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                  <p className="text-sm text-gray-900">{selectedLog.action}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Performed By</label>
                  <p className="text-sm text-gray-900">{formatPerformedBy(selectedLog.performed_by)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lead ID</label>
                  <p className="text-sm text-gray-900 font-mono">
                    {selectedLog.lead_id || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message ID</label>
                  <p className="text-sm text-gray-900 font-mono">
                    {selectedLog.message_id || '—'}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timestamp</label>
                  <p className="text-sm text-gray-900">{formatTimestamp(selectedLog.created_at)}</p>
                </div>
                </div>

                    {selectedLog.messages?.message_body && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message Content</label>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedLog.messages.message_body}</p>
                        </div>
                      </div>
                    )}

              {selectedLog.details && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Details</label>
                  <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}